import { paramEnumKeyPrefix } from '../../i18nKeys';
import type { ActionParamMetadataOutDTO, FactMetadataOutDTO, OperatorMetadataOutDTO, RuleMetadataOutDTO } from '@api/ringil-api.v2';

/**
 * What a value input needs to know. Facts and action parameters carry the same type vocabulary
 * (`FactTypeEnum` and `ParamTypeEnum` have identical members), so both render through one widget
 * set rather than two that drift apart.
 */
export interface ValueDescriptor {
  valueType: string;
  enumValues?: string[];
  unit?: string;
  entityRefType?: string;
  /**
   * The value is a list of picked entities (`add_label(labels: [...])`), so the picker is a
   * multi-select. Only action parameters set it: a LIST *fact* compared with `contains` still
   * takes one element, and `in` / `not in` get their multi-select from the operator instead.
   */
  multiple?: boolean;
  /** Where the enum values' labels live, when not under the generic `AiDispatcher.enum.*`. */
  enumKeyPrefix?: string;
  /**
   * The author may create a new entity right from the picker (a new label for `add_label`), where
   * the type supports it. Only action parameters set it: a condition on a label no expedition can
   * have yet would never match.
   */
  creatable?: boolean;
}

export const descriptorOfFact = (fact: FactMetadataOutDTO): ValueDescriptor => ({
  valueType: String(fact.factType),
  enumValues: fact.enumValues,
  unit: fact.unit,
  entityRefType: fact.entityRefType,
});

/**
 * The value of one comparison. Usually the fact decides, but an operator may name its own value
 * list (`valueEntityRefType`) — `in group` takes a country group even though the fact is a country.
 * Then the operator's list wins, and the fact's enum values and unit no longer describe the value,
 * so they are dropped. Keyed on the field, never on operator names, so a future operator with its
 * own list needs no change here.
 */
export const descriptorForComparison = (fact: FactMetadataOutDTO, operator: OperatorMetadataOutDTO | undefined): ValueDescriptor => {
  const descriptor = descriptorOfFact(fact);
  if (!operator?.valueEntityRefType) return descriptor;
  return { valueType: descriptor.valueType, entityRefType: operator.valueEntityRefType };
};

export interface MetadataRefOption {
  label: string;
  value: string;
}

/**
 * Value lists the metadata endpoint ships itself, so no API call is needed. Kept in the order the
 * backend sends (the `country_group.order` column) — not re-sorted by name.
 */
const METADATA_REF_SOURCES: Record<string, (metadata: RuleMetadataOutDTO) => MetadataRefOption[]> = {
  COUNTRY_GROUP: metadata => (metadata.countryGroups ?? []).map(group => ({ value: group.code, label: group.name })),
};

/** The list for an `entityRefType` the metadata carries, or undefined when it has to be fetched. */
export const metadataRefOptions = (metadata: RuleMetadataOutDTO | undefined, entityRefType: string | undefined): MetadataRefOption[] | undefined => {
  const source = entityRefType ? METADATA_REF_SOURCES[entityRefType] : undefined;
  if (!source) return undefined;
  return metadata ? source(metadata) : [];
};

/**
 * Fallback for action parameters whose metadata carries no `entityRefType` yet — today the carrier
 * parameters. Parameters that declare one (`labels` on `add_label` / `remove_label`) take it from
 * the metadata. Delete an entry once the backend declares the type for that parameter.
 */
export const ACTION_PARAM_REF_TYPES: Record<string, string> = {
  carrier: 'CARRIER',
  carriers: 'CARRIER',
  carrier_groups: 'CARRIER_GROUP',
};

type ParamLike = Pick<ActionParamMetadataOutDTO, 'name' | 'paramType' | 'enumValues' | 'entityRefType'>;

/** Which picker offers a parameter's values: the metadata's own answer, else the fallback map. */
export const paramEntityRefType = (param: Pick<ParamLike, 'name' | 'entityRefType'>): string | undefined => param.entityRefType ?? ACTION_PARAM_REF_TYPES[param.name];

/**
 * Parameters declare the same type vocabulary as facts, so they reuse the same widgets. The action
 * key, when given, decides where enum labels come from (`add_extra_cost.type` → `extra_cost.enum.*`).
 */
export const descriptorOfParam = (param: ParamLike, action?: string): ValueDescriptor => {
  const enumKeyPrefix = paramEnumKeyPrefix(action, param.name);
  return {
    valueType: String(param.paramType),
    enumValues: param.enumValues,
    entityRefType: paramEntityRefType(param),
    multiple: String(param.paramType) === 'LIST',
    creatable: true,
    ...(enumKeyPrefix ? { enumKeyPrefix } : {}),
  };
};

export interface Hint {
  key: string;
  text: string;
}

/**
 * Whether a comparison on free text cares about capital letters: `contains` and `starts with` ignore
 * case, `==`, `!=`, `in` and `not in` are exact — `== "fv-1"` does not match `FV-1`. Worth saying,
 * because the expedition list's search ignores case everywhere. Mirrors the backend's
 * ConditionEvaluator for every STRING fact.
 *
 * Only for text the author types: a STRING fact with an `entityRefType` (a code picked from a list)
 * or enum values is chosen from options, so its spelling is never in doubt.
 */
export const textConditionHint = (fact: Pick<FactMetadataOutDTO, 'factType' | 'entityRefType' | 'enumValues'> | undefined, operator: string | undefined): Hint | null => {
  if (String(fact?.factType) !== 'STRING' || fact.entityRefType || fact.enumValues?.length) return null;
  switch (operator) {
    case 'EQ':
    case 'NEQ':
    case 'IN':
    case 'NOT_IN':
      return { key: 'AiDispatcher.textHint.exact', text: 'Exact match: capital letters matter' };
    case 'CONTAINS':
    case 'STARTS_WITH':
      return { key: 'AiDispatcher.textHint.ignoreCase', text: 'Capital letters do not matter' };
    default:
      return null;
  }
};

/**
 * What an operator means on a LIST fact (labels, requirements, loading types…), which is not what
 * it means on a scalar: `in` is "has at least one of", not a subset test, and "all of" is several
 * `contains` joined with `and`. Mirrors `compareCollection` in the backend's ConditionEvaluator.
 */
export const listConditionHint = (factType: string | undefined, operator: string | undefined): Hint | null => {
  if (factType !== 'LIST') return null;
  switch (operator) {
    case 'CONTAINS':
      return { key: 'AiDispatcher.listHint.contains', text: 'Matches when it has this one' };
    case 'IN':
      return { key: 'AiDispatcher.listHint.in', text: 'Matches when it has at least one of these — for all of them, join several "contains" with "and"' };
    case 'NOT_IN':
      return { key: 'AiDispatcher.listHint.notIn', text: 'Matches when it has none of these' };
    case 'IS_NULL':
      return { key: 'AiDispatcher.listHint.isNull', text: 'Matches when it has none at all' };
    case 'IS_NOT_NULL':
      return { key: 'AiDispatcher.listHint.isNotNull', text: 'Matches when it has at least one' };
    default:
      return null;
  }
};

export interface PickOption {
  label: string;
  value: string;
}

/**
 * The option a create call just added. The label endpoint returns no id, so the list is reloaded and
 * compared with the one before: the option that was not there is the new one. When several appeared
 * (someone else created one meanwhile), the one with the typed name wins; when none did (the backend
 * reused an existing label of that name), the existing one with that name is picked.
 */
export const findCreatedOption = (before: PickOption[], after: PickOption[], name: string): PickOption | undefined => {
  const known = new Set(before.map(option => option.value));
  const added = after.filter(option => !known.has(option.value));
  return added.find(option => option.label === name) ?? (added.length === 1 ? added[0] : undefined) ?? after.find(option => option.label === name);
};
